import React, { useEffect, useRef, useState } from 'react';
import { Button, Divider, DrawerModal, Flex, Icon, Label, Popover, ProductTag, Switch, Tag, notification } from '../../common';
import chroma from 'chroma-js';
import tenantUtils from '@utils';
import TenantComponents from '@components';
import { useTranslation } from 'react-i18next';
import tenantData from '@data';
import tenantTheme from '@theme';
import { DubizzleFlameLogo, BayutLogoEn, BayutSmallLogo, BayutLogoAr } from '../../svg';
import { useSelector } from 'react-redux';
import { getTimeDateString } from '../../../utility/date';
import { TIME_DATE_FORMAT } from '../../../constants/formats';
import { useApplyProductModalDataOman } from '../../../hooks/useApplyProductModalDataOman';
import { getListingActions } from '../../../tenant/common/data/products';
import { isLiteExperienceURL } from '../../../utility/general';
import ListingsStats from '../../../tenant/common/components/listing/listingStats';
import { isUnifiedProfolioEnabled } from '../../../utility/user';
import { useUpdateAutoRenewMutation } from '../../../apis/listings';
import { getUnifiedListingActionTitleKey } from '../../../tenant/common/utils/unifiedListingProductCopy';

const getLogo = (icon) => {
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);
  const rtl = useSelector((state) => state.app.AppConfig.rtl);

  if (icon === 'BayutSmallLogo') {
    return isMobile ? (
      <BayutSmallLogo width="25px" height={'25px'} />
    ) : !rtl ? (
      <BayutLogoEn width="50px" style={{ marginTop: '2px' }} />
    ) : (
      <BayutLogoAr width="50px" />
    );
  }
  return isMobile ? (
    <DubizzleFlameLogo width="25px" height={'25px'} />
  ) : (
    <Icon size="50px" style={{ height: '20px', marginTop: '-2px' }} icon={rtl ? 'DubizzleLogoAr' : 'DubizzleLogo'} />
  );
};

const PlatformListing = (props) => {
  const { inlineActionsSlot } = props;
  const platformData = props?.[props?.slug] || {};

  const {
    icon,
    productsInfo = {},
    isBadge = false,
    disposition = {},
    postedOn,
    expiryDate,
    showPostedAt = false,
    stats = {},
    hidePostNowBtn = false,
    showStats = false,
    user,
  } = platformData;

  const isMultiPlatform = user?.isMultiPlatform;
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);
  const { t } = useTranslation();
  const deductionModalRef = useRef();
  const serviceDataRef = useRef();
  const isUnifiedPostListing = isUnifiedProfolioEnabled({ featureKey: 'postListing' });
  const isUnifiedListings = isUnifiedProfolioEnabled({ featureKey: 'listingsUnified' });
  const skipUnifiedPerPlatformTimeline = isUnifiedListings && props?.slug && props.slug !== 'unified';
  const hideUnifiedDubizzlePostNow = isUnifiedListings && props?.slug === 'dubizzle';
  const [updateAutoRenew] = useUpdateAutoRenewMutation();
  const [autoRenewModalVisible, setAutoRenewModalVisible] = useState(false);
  const [autoRenewLoading, setAutoRenewLoading] = useState(false);
  const [autoRenewLocalPatch, setAutoRenewLocalPatch] = useState(null);

  useEffect(() => {
    setAutoRenewLocalPatch(null);
  }, [props?.property_id]);

  const autoRenewItem =
    autoRenewLocalPatch != null
      ? { ...(props?.auto_renewable_item || {}), ...autoRenewLocalPatch }
      : props?.auto_renewable_item;
  const autoRenewApplied = !!autoRenewItem?.isApplied;

  const {
    confirmationLoading,
    selectedPaymentOption,
    paymentOptions,
    action,
    onSuccessQuotaCreditModal,
    getDeduction,
    onChangePaymentOption,
    onCancelModal,
    actionLoading,
  } = useApplyProductModalDataOman(user, deductionModalRef, serviceDataRef, isUnifiedListings);

  const unifiedQuotaModalTitleKey =
    isUnifiedListings && action?.applicableProduct
      ? getUnifiedListingActionTitleKey(action.applicableProduct)
      : null;

  const handlePostNowClick = () => {
    getDeduction(
      'credit',
      props,
      getListingActions(props?.slug === 'dubizzle' ? 'basic-listing-dubizzle' : 'basic-listing', props),
      props?.slug,
    );
  };

  const renderMobileView = () => {
    const innerMobileBlock = (
        <Flex align={'center'} justify="space-between" gap={'10px'} vertical={false} wrap={isMultiPlatform}>
          <Flex
            vertical={false}
            justify={'space-between'}
            gap={'4px'}
            align={!isMultiPlatform ? 'center' : null}
            wrap={true}
            className="w-100"
          >
            <Flex vertical={true} align={'start'} gap={'6px'}>
            {!isUnifiedPostListing && 
              <Flex align="center" gap="6px">
                <div style={{ lineHeight: 1 }}>{getLogo(icon)}</div>

                {isBadge && (
                  <>
                    <ProductTag
                      {...tenantData.getTagProps(productsInfo)}
                      iconProps={{ ...tenantData.getTagProps(productsInfo)?.iconProps, size: '1.2em' }}
                      tagSpace="3px 6px"
                    />
                  </>
                )}

                <Tag
                  style={{
                    '--tag-color': disposition?.color || '#fff',
                    '--tag-bg': disposition?.color
                      ? chroma.mix(disposition.color, '#fff', 0.9).saturate(0.3)
                      : '#767676',
                    fontWeight: '700',
                    fontSize: '10px',
                    paddingBlock: '2px',
                    paddingInline: '6px',
                  }}
                >
                  {disposition?.label}
                </Tag>
              </Flex>
              }
              {disposition?.description && !isUnifiedListings && (
                <div style={{ fontStyle: 'italic', color: tenantTheme['gray800'] }}>
                  {tenantUtils.getLocalisedString(disposition, 'description')}
                </div>
              )}

              {showPostedAt && !skipUnifiedPerPlatformTimeline &&
                (isUnifiedListings && isMobile && inlineActionsSlot ? (
                  <Flex align="center" justify="space-between" gap="10px" wrap="wrap" style={{ width: '100%' }}>
                    <Flex vertical gap="8px" align="start" style={{ flex: '1 1 0', minWidth: 0, maxWidth: '100%' }}>
                      <Flex gap="6px" align="center" wrap="wrap" style={{ width: '100%' }}>
                        <div className="text-muted" style={{ flexShrink: 0 }}>
                          {t('Expiring on')}
                        </div>
                        <Flex gap="4px" align="center" style={{ flexWrap: 'nowrap', flexShrink: 0 }}>
                          <div className="text-default fw-600" style={{ whiteSpace: 'nowrap' }}>
                            {getTimeDateString(expiryDate) || '-'}
                          </div>
                          <Popover content={<div>{getTimeDateString(expiryDate, TIME_DATE_FORMAT)}</div>} action="hover">
                            <span style={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0 }}>
                              <Icon
                                icon="AiOutlineInfoCircle"
                                size="1em"
                                color={tenantTheme['primary-color']}
                                style={{ marginTop: '2px' }}
                              />
                            </span>
                          </Popover>
                        </Flex>
                      </Flex>
                      <Flex gap="6px" align="center">
                        <Switch
                          switchOnly
                          name="auto-renew"
                          size="small"
                          value={autoRenewApplied}
                          loading={autoRenewLoading}
                          onChange={() => setAutoRenewModalVisible(true)}
                        />
                        <Label htmlFor="auto-renew" className="fz-10" color={tenantTheme['primary-color']}>
                          {t('AUTO-RENEW')}
                        </Label>
                      </Flex>
                    </Flex>
                    <Flex align="center" wrap="wrap" style={{ flex: '0 1 auto', minWidth: 0 }}>
                      {inlineActionsSlot}
                    </Flex>
                  </Flex>
                ) : isUnifiedListings && isMobile ? (
                  <Flex vertical gap="6px" align="start">
                    <Flex gap="6px" align="center" wrap="wrap" style={{ width: '100%' }}>
                      <div className="text-muted" style={{ flexShrink: 0 }}>
                        {t('Expiring on')}
                      </div>
                      <Flex gap="4px" align="center" style={{ flexWrap: 'nowrap', flexShrink: 0 }}>
                        <div className="text-default fw-600" style={{ whiteSpace: 'nowrap' }}>
                          {getTimeDateString(expiryDate) || '-'}
                        </div>
                        <Popover content={<div>{getTimeDateString(expiryDate, TIME_DATE_FORMAT)}</div>} action="hover">
                          <span style={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0 }}>
                            <Icon
                              icon="AiOutlineInfoCircle"
                              size="1em"
                              color={tenantTheme['primary-color']}
                              style={{ marginTop: '2px' }}
                            />
                          </span>
                        </Popover>
                      </Flex>
                    </Flex>
                    <Flex gap="6px" align="center">
                      <Switch
                        switchOnly
                        name="auto-renew"
                        size="small"
                        value={autoRenewApplied}
                        loading={autoRenewLoading}
                        onChange={() => setAutoRenewModalVisible(true)}
                      />
                      <Label htmlFor="auto-renew" className="fz-10" color={tenantTheme['primary-color']}>
                        {t('AUTO-RENEW')}
                      </Label>
                    </Flex>
                  </Flex>
                ) : (
                  <Flex vertical gap="2px" align="start">
                    <Flex gap="4px">
                      <div className="text-muted">{t('Posted on')}</div>
                      <div className="text-default fw-600">{getTimeDateString(postedOn) || '-'}</div>
                      <Popover content={<div>{getTimeDateString(postedOn, TIME_DATE_FORMAT)}</div>} action="hover">
                        <>
                          <Icon
                            icon="AiOutlineInfoCircle"
                            size="1em"
                            color={tenantTheme['primary-color']}
                            style={{ marginTop: '4px' }}
                          />
                        </>
                      </Popover>
                    </Flex>
                  </Flex>
                ))}
              {!showPostedAt && isUnifiedListings && isMobile && inlineActionsSlot && (
                <Flex align="center" gap="8px" wrap="wrap" style={{ width: '100%' }}>
                  {inlineActionsSlot}
                </Flex>
              )}
            </Flex>
            {showStats && !isUnifiedListings && <ListingsStats disposition={disposition} stats={stats} {...props} />}

            {!hidePostNowBtn && !isUnifiedListings && (
              <Button
                type="primary"
                icon="MdOutlineAddLocationAlt"
                style={{
                  maxWidth: !showStats && '180px',
                  '--btn-bg-color': props?.slug === 'dubizzle' && tenantTheme['dubizzle-primary'],
                }}
                onClick={handlePostNowClick}
                block
                loading={actionLoading}
              >
                {t('Post Now')}
              </Button>
            )}
          </Flex>
        </Flex>
    );

    return (
      <>
        {innerMobileBlock}
        {!(isUnifiedListings && isMobile) && <Divider style={{ marginBlock: '16px' }} />}
      </>
    );
  };

  const renderDesktopView = () => {
    const hidePostNowInUnifiedTable = isUnifiedListings && !isMobile;
    const hasUnifiedPlatformColumnContent =
      !isUnifiedPostListing ||
      (disposition?.description && !isUnifiedListings) ||
      (showStats && !isUnifiedListings) ||
      (showPostedAt && !skipUnifiedPerPlatformTimeline) ||
      (!hidePostNowBtn && !hideUnifiedDubizzlePostNow && !hidePostNowInUnifiedTable);

    if (isUnifiedListings && !hasUnifiedPlatformColumnContent) {
      return null;
    }

    return (
      <Flex align={isLiteExperienceURL() ? 'center' : 'start'} justify="space-between" gap={'12px'} vertical={true}>
        <Flex
          vertical={isMultiPlatform}
          justify={!isMultiPlatform ? 'space-between' : null}
          gap={'10px'}
          align={!isMultiPlatform ? 'center' : null}
          wrap={true}
          className="w-100"
          style={!user?.is_package_user ? { maxWidth: '250px' } : {}}
        >
          <Flex vertical={true} align={'start'} gap={!isMultiPlatform ? '6px' : '14px'}>
          {!isUnifiedPostListing && 
            <Flex align="center" gap="8px">
              <div style={{ lineHeight: 1 }}>{getLogo(icon)}</div>

              {isBadge && (
                <>
                  <ProductTag
                    {...tenantData.getTagProps(productsInfo)}
                    iconProps={{ ...tenantData.getTagProps(productsInfo)?.iconProps, size: '1.2em' }}
                    tagSpace="3px 6px"
                  />
                </>
              )}

              <Tag
                style={{
                  '--tag-color': disposition?.color || '#fff',
                  '--tag-bg': disposition?.color ? chroma.mix(disposition.color, '#fff', 0.9).saturate(0.3) : '#767676',
                  fontWeight: '700',
                  fontSize: '10px',
                  paddingBlock: '2px',
                  paddingInline: '6px',
                }}
              >
                {disposition?.label}
              </Tag>
            </Flex>
            }

            {disposition?.description && !isUnifiedListings && (
              <div style={{ fontStyle: 'italic', color: tenantTheme['gray800'] }}>
                {tenantUtils.getLocalisedString(disposition, 'description')}
              </div>
            )}
          </Flex>
          {showStats && !isUnifiedListings && <ListingsStats disposition={disposition} stats={stats} {...props} />}

          {showPostedAt && !skipUnifiedPerPlatformTimeline && (
            <>
              <Flex vertical gap="2px" align="start">
                <Flex gap="4px">
                  <div className="text-muted">{t(isUnifiedListings ? 'Posted' : 'Posted on')}</div>

                  <div className="text-default fw-600">{getTimeDateString(postedOn) || '-'}</div>
                  <Popover content={<div>{getTimeDateString(postedOn, TIME_DATE_FORMAT)}</div>} action="hover">
                    <>
                      <Icon
                        icon="AiOutlineInfoCircle"
                        size="1em"
                        color={tenantTheme['primary-color']}
                        style={{ marginTop: '4px' }}
                      />
                    </>
                  </Popover>
                </Flex>
                {isUnifiedListings && (
                  <Flex gap="4px">
                    <div className="text-muted">{t('Expiring')}</div>
                    <div className="text-default fw-600">{getTimeDateString(expiryDate) || '-'}</div>
                  </Flex>
                )}
                {isUnifiedListings && (
                  <Flex gap="6px" align="center" style={{ marginTop: 2 }}>
                    <Switch
                      switchOnly
                      name="auto-renew"
                      size="small"
                      value={autoRenewApplied}
                      loading={autoRenewLoading}
                      onChange={() => setAutoRenewModalVisible(true)}
                    />
                    <Label htmlFor="auto-renew" className="fz-10" color={tenantTheme['primary-color']}>
                      {t('AUTO-RENEW')}
                    </Label>
                  </Flex>
                )}
              </Flex>
            </>
          )}
          {!hidePostNowBtn && !hideUnifiedDubizzlePostNow && !hidePostNowInUnifiedTable && (
            <Button
              type="primary"
              icon="MdOutlineAddLocationAlt"
              style={{
                maxWidth: '175px',
                '--btn-bg-color': props?.slug === 'dubizzle' && tenantTheme['dubizzle-primary'],
              }}
              onClick={handlePostNowClick}
              block
              loading={actionLoading}
            >
              {t('Post Now')}
            </Button>
          )}
        </Flex>
      </Flex>
    );
  };
  return (
    <>
      {isUnifiedListings && (
        <DrawerModal
          title={`${autoRenewApplied ? t('Disable') : t('Enable')} ${t('Auto Renew')}`}
          visible={autoRenewModalVisible}
          onOk={async () => {
            setAutoRenewLoading(true);
            const autoRenewId = autoRenewApplied ? autoRenewItem?.id : null;
            const nextApplied = !autoRenewApplied;
            setAutoRenewLocalPatch({
              isApplied: nextApplied,
              id: nextApplied ? autoRenewItem?.id ?? null : null,
              renewing_on: nextApplied ? autoRenewItem?.renewing_on ?? null : null,
            });
            try {
              const response = await updateAutoRenew({
                autoRenewId,
                autoRenewableId: props?.property_id,
                autoRenewableType: 'Listing',
                productId: tenantData.getListingActions('basic-listing')?.id,
              });
              if (response?.error) {
                setAutoRenewLocalPatch(null);
                notification.error(response?.error);
                return;
              }
              const payload = response?.data;
              if (payload && typeof payload === 'object') {
                setAutoRenewLocalPatch({
                  isApplied: !!payload.isApplied,
                  id: payload.id ?? null,
                  renewing_on: payload.renewing_on ?? null,
                });
              }
              notification.success(t('Updated Successfully'));
              setAutoRenewModalVisible(false);
            } catch {
              setAutoRenewLocalPatch(null);
              notification.error(t('Something went wrong'));
            } finally {
              setAutoRenewLoading(false);
            }
          }}
          onCancel={() => setAutoRenewModalVisible(false)}
          loading={autoRenewLoading}
          height="auto"
        >
          {!autoRenewApplied
            ? t(
                "By turning the 'Auto-Renew' feature ON. The system will automatically renew the listing when it reaches its expiry, if you have enough credits available",
              )
            : t('Are you sure you want to disable auto renew for this listing')}
        </DrawerModal>
      )}
      <TenantComponents.QuotaCreditModal
        ref={deductionModalRef}
        title={
          unifiedQuotaModalTitleKey ? t(unifiedQuotaModalTitleKey) : action?.applicableProduct?.requestTitle
        }
        action={action}
        okText={isUnifiedListings ? 'Apply Now' : action?.isSufficient ? 'Submit' : 'Continue'}
        onSuccess={onSuccessQuotaCreditModal}
        onCancel={onCancelModal}
        loading={confirmationLoading}
        serviceDataRef={serviceDataRef}
        selectedPaymentOption={selectedPaymentOption}
        paymentOptions={paymentOptions}
        onChangePaymentOption={onChangePaymentOption}
        showInsufficientCreditsAlert
        showInfoMessage
        hideDataTable
        unifiedCreditsOnly={isUnifiedListings}
      />
      {isMobile ? renderMobileView() : renderDesktopView()}
    </>
  );
};

export default PlatformListing;
